import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Check, Clock3 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import API from "../services/api";

const BET_TYPES = [
  { key: "single",       label: "Single Digit",         emoji: "1️⃣",  bg: "#7c3aed", rate: "1-10" },
  { key: "sp",           label: "Single Pana",           emoji: "🃏",  bg: "#16a34a", rate: "1-160" },
  { key: "sp_motor",     label: "SP Motor",              emoji: "⚙️",  bg: "#f97316", rate: "1-160" },
  { key: "sp_dp_tp",     label: "SP DP TP",              emoji: "🔢",  bg: "#0891b2", rate: "1-160" },
  { key: "dp",           label: "Double Pana",           emoji: "🎭",  bg: "#7c3aed", rate: "1-320" },
  { key: "tp",           label: "Triple Pana",           emoji: "💎",  bg: "#ea580c", rate: "1-1000" },
  { key: "dp_motor",     label: "DP Motor",              emoji: "🔧",  bg: "#16a34a", rate: "1-320" },
  { key: "two_digit_panel", label: "Two Digit Panel\n(CP,SR)", emoji: "🎯", bg: "#7c3aed", rate: "1-160" },
];

function StarlinePlayPage() {
  const { id, mode: modeParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [marketId, setMarketId] = useState(id || "");
  const [timeSlot, setTimeSlot] = useState(searchParams.get("slot") || "");
  const [mode, setMode] = useState("");
  const [digitAmounts, setDigitAmounts] = useState(Array(10).fill(""));
  const [bulkDigits, setBulkDigits] = useState([]);
  const [bulkAmount, setBulkAmount] = useState("");
  const [panna, setPanna] = useState("");
  const [pannaAmount, setPannaAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [ankStatus, setAnkStatus] = useState({ ank_limit: null, used_digits: [], used_count: 0, remaining: null });
  const requestedSlot = searchParams.get("slot") || "";
  const requestedMode = String(modeParam || "").trim();
  const busyRef = useRef(false);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    if (!requestedMode) { setMode(""); return; }
    const valid = BET_TYPES.some((t) => t.key === requestedMode) ? requestedMode : "";
    setMode(valid);
  }, [requestedMode]);

  useEffect(() => {
    let mounted = true;
    API.get("/starline/control")
      .then((res) => {
        if (!mounted) return;
        const marketRows = Array.isArray(res.data?.markets) ? res.data.markets : [];
        const slotRows = Array.isArray(res.data?.slots)
          ? res.data.slots.filter((slot) => Number(slot.is_active) === 1)
          : [];
        const selectedMarketId = String(id || marketRows[0]?.id || "");
        const marketSlots = slotRows.filter((s) => String(s.market_id) === selectedMarketId);
        const resolvedSlot = marketSlots.find((s) => s.time_slot === requestedSlot)?.time_slot || marketSlots[0]?.time_slot || "";
        setMarkets(marketRows);
        setSlots(slotRows);
        setMarketId(selectedMarketId);
        setTimeSlot(resolvedSlot);
        if (resolvedSlot && resolvedSlot !== requestedSlot) {
          const p = new URLSearchParams(); p.set("slot", resolvedSlot);
          setSearchParams(p, { replace: true });
        }
      })
      .catch(() => { if (!mounted) return; setMarkets([]); setSlots([]); });
    return () => { mounted = false; };
  }, [id, requestedSlot, setSearchParams]);

  const marketSlots = slots.filter((s) => String(s.market_id) === String(marketId));
  const market = markets.find((m) => String(m.id) === String(marketId)) || markets[0];
  const activeType = BET_TYPES.find((t) => t.key === mode);

  useEffect(() => {
    if (!user?.id || !marketId || !timeSlot) return;
    API.get(`/users/${user.id}/ank-status?market_id=${encodeURIComponent(marketId)}&session=OPEN&time_slot=${encodeURIComponent(timeSlot)}`)
      .then((res) => setAnkStatus(res.data || { ank_limit: null, used_digits: [], used_count: 0, remaining: null }))
      .catch(() => setAnkStatus({ ank_limit: null, used_digits: [], used_count: 0, remaining: null }));
  }, [marketId, timeSlot, user]);

  const totalAmount = useMemo(() => {
    if (!mode) return 0;
    if (mode === "single") return digitAmounts.reduce((s, v) => s + Number(v || 0), 0);
    if (["single_bulk", "sp_motor", "dp_motor"].includes(mode)) return bulkDigits.length * Number(bulkAmount || 0);
    return Number(pannaAmount || 0);
  }, [mode, digitAmounts, bulkDigits, bulkAmount, pannaAmount]);

  const usedDigitSet = useMemo(() => new Set((ankStatus.used_digits || []).map((v) => String(v))), [ankStatus.used_digits]);

  const resetInputs = () => {
    setDigitAmounts(Array(10).fill(""));
    setBulkDigits([]);
    setBulkAmount("");
    setPanna("");
    setPannaAmount("");
  };

  const navigateTo = (nextSlot, nextMode) => {
    const p = new URLSearchParams();
    if (nextSlot) p.set("slot", nextSlot);
    const path = nextMode ? `/starline/${marketId || id}/play/${nextMode}` : `/starline/${marketId || id}/play`;
    navigate(`${path}${p.toString() ? `?${p}` : ""}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const makeBet = (number, amount, type) => ({ user_id: user.id, market_id: marketId, number, amount, type, time_slot: timeSlot });

  const submit = async () => {
    if (busyRef.current || busy || !user?.id || !marketId || !timeSlot || !mode) return;
    const payload = [];
    if (mode === "single") {
      digitAmounts.forEach((amount, digit) => { if (Number(amount || 0) > 0) payload.push(makeBet(String(digit), Number(amount), "single")); });
    } else if (["single_bulk", "sp_motor", "dp_motor"].includes(mode)) {
      if (!bulkDigits.length || Number(bulkAmount || 0) <= 0) { alert("Select digits and enter amount"); return; }
      bulkDigits.forEach((digit) => payload.push(makeBet(String(digit), Number(bulkAmount), mode)));
    } else if (panna && Number(pannaAmount || 0) > 0) {
      payload.push(makeBet(panna, Number(pannaAmount), mode));
    }
    if (!payload.length) { alert("Enter bet details"); return; }
    busyRef.current = true;
    setBusy(true);
    try {
      for (const item of payload) await API.post("/place-bet", item);
      resetInputs();
      alert("Starline bid placed!");
      navigate(`/starline/${marketId}`);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to place bid");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const isSingleMode = mode === "single";
  const isBulkMode = ["single_bulk", "sp_motor", "dp_motor", "sp_dp_tp"].includes(mode);
  const isPannaMode = ["sp", "dp", "tp", "two_digit_panel"].includes(mode);

  return (
    <div style={page}>
      {/* Header */}
      <header style={header}>
        <button type="button" style={backBtn} onClick={() => {
          if (mode) { navigateTo(timeSlot, ""); resetInputs(); return; }
          navigate(`/starline/${marketId || id}`, { replace: true });
        }}>
          <ArrowLeft size={22} color="#fff" />
        </button>
        <div style={headerCenter}>
          <span style={headerTitle}>
            {mode ? activeType?.label?.replace("\n", " ") : (market?.name || "Starline")}
          </span>
          {market?.name && mode && <span style={headerMarket}>{market.name}</span>}
        </div>
        <div style={{ width: 38 }} />
      </header>

      {/* Game rates strip (only on type select screen) */}
      {!mode && (
        <div style={ratesStrip}>
          <div style={ratesBox}>
            <span style={ratesText}>Single Digit: <strong>1-10</strong></span>
            <span style={ratesDivider}>|</span>
            <span style={ratesText}>Single Pana: <strong>1-160</strong></span>
          </div>
          <div style={ratesBox}>
            <span style={ratesText}>Double Pana: <strong>1-320</strong></span>
            <span style={ratesDivider}>|</span>
            <span style={ratesText}>Triple Pana: <strong>1-1000</strong></span>
          </div>
        </div>
      )}

      {/* Slot + market selectors */}
      <div style={selRow}>
        {markets.length > 1 && (
          <select style={selInput} value={marketId} onChange={(e) => setMarketId(e.target.value)}>
            {markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <div style={slotChipRow}>
          <Clock3 size={14} color="#f97316" />
          <select style={slotSelect} value={timeSlot} onChange={(e) => { setTimeSlot(e.target.value); navigateTo(e.target.value, mode); }}>
            {marketSlots.map((s) => <option key={s.id} value={s.time_slot}>{s.time_slot}</option>)}
          </select>
        </div>
      </div>

      <main style={main}>
        {/* Type selection grid */}
        {!mode && (
          <>
            <div style={sectionLabel}>Select Bet Type</div>
            <div style={typeGrid}>
              {BET_TYPES.map((t) => (
                <button key={t.key} type="button" style={typeCard} onClick={() => { resetInputs(); navigateTo(timeSlot, t.key); }}>
                  <div style={{ ...typeIconCircle, background: t.bg }}>
                    <span style={typeEmoji}>{t.emoji}</span>
                  </div>
                  <span style={typeName}>{t.label}</span>
                  <span style={typeRate}>Rate: {t.rate}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Single digit form */}
        {isSingleMode && (
          <div style={formCard}>
            <div style={digitGrid}>
              {digitAmounts.map((amount, digit) => (
                <div key={digit} style={digitBox}>
                  <div style={usedDigitSet.has(String(digit)) ? digitLabelUsed : digitLabelStyle}>{digit}</div>
                  <input
                    value={amount}
                    onChange={(e) => {
                      const next = [...digitAmounts];
                      next[digit] = e.target.value.replace(/\D/g, "");
                      setDigitAmounts(next);
                    }}
                    inputMode="numeric"
                    placeholder="₹"
                    style={digitInput}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bulk digit form */}
        {isBulkMode && (
          <div style={formCard}>
            <div style={sectionLabel}>Select Digits</div>
            <div style={bulkGrid}>
              {Array.from({ length: 10 }, (_, digit) => (
                <button
                  key={digit}
                  type="button"
                  style={bulkDigits.includes(digit) ? bulkDigitActive : bulkDigitBtn}
                  onClick={() => setBulkDigits((prev) => prev.includes(digit) ? prev.filter((d) => d !== digit) : [...prev, digit])}
                >
                  {digit}{bulkDigits.includes(digit) && <Check size={12} />}
                </button>
              ))}
            </div>
            <input value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Amount per digit (₹)" style={wideInput} />
          </div>
        )}

        {/* Panna form */}
        {isPannaMode && (
          <div style={formCard}>
            <label style={fieldLabel}>{activeType?.label?.replace("\n", " ")} *</label>
            <input value={panna} onChange={(e) => setPanna(e.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" placeholder="Enter number" style={wideInput} />
            <label style={fieldLabel}>Amount *</label>
            <input value={pannaAmount} onChange={(e) => setPannaAmount(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="₹ Amount" style={wideInput} />
          </div>
        )}
      </main>

      {/* Footer */}
      {mode && (
        <footer style={footer}>
          <div style={footerInfo}>
            <span style={footerLabel}>Total Amount</span>
            <strong style={footerAmount}>₹{totalAmount}</strong>
          </div>
          <button
            type="button"
            style={{ ...placeBetBtn, opacity: !totalAmount || busy ? 0.5 : 1 }}
            disabled={busy || !totalAmount}
            onClick={submit}
          >
            {busy ? "Placing..." : "Place Bid"}
          </button>
        </footer>
      )}
    </div>
  );
}

/* styles */
const page = { minHeight: "100vh", background: "#f3f4f7", fontFamily: "'Segoe UI', Inter, system-ui, sans-serif", paddingBottom: 80 };

const header = {
  background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
  height: 58,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 14px",
  position: "sticky",
  top: 0,
  zIndex: 10,
};
const backBtn = { width: 38, height: 38, borderRadius: "50%", border: 0, background: "rgba(255,255,255,0.2)", display: "grid", placeItems: "center", cursor: "pointer", flexShrink: 0 };
const headerCenter = { display: "flex", flexDirection: "column", alignItems: "center", flex: 1 };
const headerTitle = { fontSize: 16, fontWeight: 800, color: "#fff" };
const headerMarket = { fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 1 };

const ratesStrip = { background: "#fff", padding: "10px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, borderBottom: "1px solid #f1f5f9" };
const ratesBox = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" };
const ratesText = { fontSize: 12, color: "#374151" };
const ratesDivider = { color: "#d1d5db", fontSize: 12 };

const selRow = { background: "#fff", padding: "10px 14px", display: "flex", gap: 10, alignItems: "center", borderBottom: "1px solid #f1f5f9" };
const selInput = { flex: 1, height: 38, border: "1px solid #e5e7eb", borderRadius: 10, padding: "0 10px", fontSize: 13, background: "#f8fafc" };
const slotChipRow = { display: "flex", alignItems: "center", gap: 6, height: 38, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "0 10px" };
const slotSelect = { border: 0, background: "transparent", fontSize: 13, fontWeight: 700, color: "#f97316", outline: "none" };

const main = { padding: "14px" };
const sectionLabel = { fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 12 };

const typeGrid = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 };
const typeCard = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "16px 8px 14px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
};
const typeIconCircle = { width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center" };
const typeEmoji = { fontSize: 22 };
const typeName = { fontSize: 11, fontWeight: 700, color: "#1e293b", textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.3 };
const typeRate = { fontSize: 10, color: "#6b7280", fontWeight: 600 };

const formCard = { background: "#fff", borderRadius: 18, padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", display: "grid", gap: 12 };
const digitGrid = { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 };
const digitBox = { display: "flex", flexDirection: "column", gap: 6, alignItems: "center" };
const digitLabelStyle = { width: 40, height: 40, borderRadius: 12, background: "#ede9fe", color: "#7c3aed", display: "grid", placeItems: "center", fontSize: 16, fontWeight: 800 };
const digitLabelUsed = { ...digitLabelStyle, background: "#dcfce7", color: "#16a34a" };
const digitInput = { width: "100%", height: 38, border: "1.5px solid #e5e7eb", borderRadius: 10, textAlign: "center", fontSize: 13, fontWeight: 700, background: "#f8fafc", outline: "none", boxSizing: "border-box" };
const bulkGrid = { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8 };
const bulkDigitBtn = { height: 42, border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#f8fafc", color: "#374151", fontWeight: 800, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 4, cursor: "pointer" };
const bulkDigitActive = { ...bulkDigitBtn, background: "#7c3aed", color: "#fff", border: "1.5px solid #6d28d9" };
const wideInput = { width: "100%", height: 48, border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "0 14px", boxSizing: "border-box", fontSize: 15, fontWeight: 600, background: "#f8fafc", outline: "none" };
const fieldLabel = { fontSize: 12, fontWeight: 700, color: "#374151" };

const footer = { position: "fixed", bottom: 0, left: 0, right: 0, height: 64, background: "#fff", borderTop: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px" };
const footerInfo = { display: "flex", flexDirection: "column" };
const footerLabel = { fontSize: 11, color: "#6b7280" };
const footerAmount = { fontSize: 18, fontWeight: 900, color: "#1e293b" };
const placeBetBtn = { height: 44, padding: "0 28px", border: 0, borderRadius: 12, background: "linear-gradient(135deg, #7c3aed, #5b21b6)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" };

export default StarlinePlayPage;
